import { useState } from 'react';

/**
 * 除錯模式 Hook
 * 管理除錯模式狀態、密碼驗證和錯誤嘗試限制
 */
export const useDebugMode = () => {
  const [isDebugMode, setIsDebugMode] = useState(() => {
    return sessionStorage.getItem('orlo-debug-mode') === 'true';
  });

  const [failedAttempts, setFailedAttempts] = useState(() => {
    const stored = sessionStorage.getItem('orlo-debug-attempts');
    return stored ? parseInt(stored, 10) : 0;
  });

  /**
   * 計算今天的除錯密碼
   * 格式：當天日期數字 + 20221111
   * 例如：2025年7月9日 = 20250709 + 20221111 = 40471820
   */
  const calculateTodaysPassword = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    
    const dateNumber = parseInt(`${year}${month}${day}`);
    const fixedNumber = 20221111;
    
    return (dateNumber + fixedNumber).toString();
  };

  /**
   * 驗證密碼
   */
  const validatePassword = (inputPassword: string): boolean => {
    const expectedPassword = calculateTodaysPassword();
    return inputPassword === expectedPassword;
  };

  /**
   * 嘗試啟用除錯模式
   * @param password 用戶輸入的密碼
   * @returns 是否成功啟用
   */
  const attemptEnableDebugMode = (password: string): boolean => {
    if (failedAttempts >= 3) {
      return false;
    }

    if (validatePassword(password)) {
      setIsDebugMode(true);
      setFailedAttempts(0);
      sessionStorage.setItem('orlo-debug-mode', 'true');
      sessionStorage.removeItem('orlo-debug-attempts');
      
      // 記錄成功啟用
      const timestamp = new Date().toISOString();
      
      return true;
    } else {
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      sessionStorage.setItem('orlo-debug-attempts', newAttempts.toString());
      
      return false;
    }
  };

  /**
   * 停用除錯模式
   */
  const disableDebugMode = () => {
    setIsDebugMode(false);
    sessionStorage.removeItem('orlo-debug-mode');
  };

  /**
   * 重置失敗嘗試次數（僅供測試）
   */
  const resetAttempts = () => {
    setFailedAttempts(0);
    sessionStorage.removeItem('orlo-debug-attempts');
  };

  return {
    isDebugMode,
    failedAttempts,
    maxAttempts: 3,
    isLocked: failedAttempts >= 3,
    attemptEnableDebugMode,
    disableDebugMode,
    resetAttempts
  };
};